import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { localizeAuthMessage } from "@/lib/authMessages";
import { buildPublicAssetUrl } from "@/lib/appPaths";
import { sanitizeLoginReturnTo } from "@/lib/authRedirect";
import { resolveOrganizationDestination } from "@/lib/organizationRouting";
import { Lock, Mail } from "lucide-react";
import { FormEvent, useState, useCallback, useRef, useEffect, useMemo } from "react";
import { Link, useLocation } from "wouter";

export default function Login() {
  const { t, language } = useLanguage();
  const { signIn } = useAuth();
  const [location, setLocation] = useLocation();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const bgRef = useRef<HTMLDivElement>(null);
  const blob1Ref = useRef<HTMLDivElement>(null);
  const blob2Ref = useRef<HTMLDivElement>(null);
  const blob3Ref = useRef<HTMLDivElement>(null);
  const returnTo = useMemo(() => {
    const queryFromLocation = location.includes("?") ? location.slice(location.indexOf("?") + 1) : "";
    const query = queryFromLocation || (typeof window === "undefined" ? "" : window.location.search.replace(/^\?/, ""));
    const params = new URLSearchParams(query);
    return sanitizeLoginReturnTo(params.get("returnTo"));
  }, [location]);

  // 目标位置（鼠标位置）
  const targetRef = useRef({ x: 0, y: 0 });
  // 当前插值位置（三个光晕各自独立）
  const currentRef = useRef([
    { x: 0, y: 0 },
    { x: 0, y: 0 },
    { x: 0, y: 0 },
  ]);
  const rafRef = useRef<number | null>(null);

  // 每个光晕的移动幅度和缓动速度不同，产生层次感
  const blobs = [
    { ref: blob1Ref, strength: 60,  ease: 0.06 },
    { ref: blob2Ref, strength: -90, ease: 0.04 },
    { ref: blob3Ref, strength: 45,  ease: 0.08 },
  ];

  useEffect(() => {
    const animate = () => {
      blobs.forEach(({ ref, strength, ease }, i) => {
        const cur = currentRef.current[i];
        const tgt = targetRef.current;
        cur.x += (tgt.x * strength - cur.x) * ease;
        cur.y += (tgt.y * strength - cur.y) * ease;
        if (ref.current) {
          ref.current.style.transform = `translate(${cur.x}px, ${cur.y}px)`;
        }
      });
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const el = bgRef.current;
    if (!el) return;
    const { left, top, width, height } = el.getBoundingClientRect();
    targetRef.current = {
      x: (e.clientX - left) / width - 0.5,
      y: (e.clientY - top) / height - 0.5,
    };
  }, []);

  const canSubmit = identifier.trim() && password.trim();
  const auSsoEnabled = String(import.meta.env.VITE_AU_SSO_ENABLED || "").toLowerCase() === "true";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const result = await signIn({ identifier, password, rememberMe });
      if (!result.ok) {
        setErrorMessage(
          localizeAuthMessage(
            language,
            t("登录失败，请检查账号或密码", "Login failed. Please check your account or password."),
            result.message
          )
        );
        return;
      }
      setLocation(resolveOrganizationDestination(returnTo, result.user?.organization?.code), { replace: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      ref={bgRef}
      className="fixed inset-0 flex items-center justify-center overflow-y-auto"
      onMouseMove={handleMouseMove}
      style={{ backgroundColor: "#f4f9f4" }}
    >
      <style>{`
        @keyframes tm-breathe-1 { 0%,100% { opacity:1; } 50% { opacity:0.45; } }
        @keyframes tm-breathe-2 { 0%,100% { opacity:1; } 50% { opacity:0.35; } }
        @keyframes tm-breathe-3 { 0%,100% { opacity:1; } 50% { opacity:0.50; } }
      `}</style>
      {/* 光晕1：左上 */}
      <div ref={blob1Ref} style={{
        position: "absolute", left: "12%", top: "25%", width: "65%", height: "55%",
        borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(ellipse, rgba(96,175,106,0.18) 0%, transparent 70%)",
        animation: "tm-breathe-1 2s ease-in-out infinite",
      }} />
      {/* 光晕2：右上 */}
      <div ref={blob2Ref} style={{
        position: "absolute", right: "12%", top: "12%", width: "60%", height: "70%",
        borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(ellipse, rgba(140,205,145,0.14) 0%, transparent 65%)",
        animation: "tm-breathe-2 2.5s ease-in-out infinite 0.4s",
      }} />
      {/* 光晕3：底部 */}
      <div ref={blob3Ref} style={{
        position: "absolute", left: "15%", bottom: "-10%", width: "80%", height: "55%",
        borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(ellipse, rgba(110,188,118,0.12) 0%, transparent 70%)",
        animation: "tm-breathe-3 1.8s ease-in-out infinite 0.2s",
      }} />
      <div className="relative z-10 w-full max-w-[360px] px-4 flex flex-col items-center gap-7 py-10 mb-32">
        {/* Logo + Title 紧靠在一起 */}
        <div className="flex flex-col items-center gap-3">
          <img
            src={buildPublicAssetUrl("/logo_character.png")}
            alt="TeachMaster"
            className="w-24 object-contain"
          />

          {/* Title */}
          <div className="text-center space-y-1">
            <h1 className="text-2xl font-medium text-foreground">
              {t("登录或注册", "Sign In or Sign Up")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t("开始你的课程共创之旅", "Start your course co-creation journey")}
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="w-full flex flex-col gap-3">
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
            <Input
              id="email"
              type="text"
              placeholder={t("邮箱或用户名", "Email or username")}
              value={identifier}
              onChange={e => {
                setIdentifier(e.target.value);
                if (errorMessage) setErrorMessage("");
              }}
              className="h-11 rounded-xl pl-9 bg-white border-border/50 shadow-sm"
            />
          </div>

          <div className="relative">
            <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
            <Input
              id="password"
              type="password"
              placeholder={t("密码", "Password")}
              value={password}
              onChange={e => {
                setPassword(e.target.value);
                if (errorMessage) setErrorMessage("");
              }}
              className="h-11 rounded-xl pl-9 bg-white border-border/50 shadow-sm"
            />
          </div>

          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Checkbox
                id="remember"
                checked={rememberMe}
                onCheckedChange={checked => setRememberMe(Boolean(checked))}
              />
              <Label htmlFor="remember" className="font-normal text-muted-foreground cursor-pointer">
                {t("记住我", "Remember me")}
              </Label>
            </div>
            <Link
              href="/forgot-password"
              className="text-muted-foreground hover:text-foreground hover:underline transition-colors"
            >
              {t("忘记密码？", "Forgot password?")}
            </Link>
          </div>

          {errorMessage && (
            <Alert variant="destructive">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            className="h-11 w-full rounded-xl mt-1 shadow-sm"
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? t("登录中...", "Signing in...") : t("登录", "Sign In")}
          </Button>
        </form>

        {auSsoEnabled && (
          <Button
            type="button"
            variant="outline"
            className="h-11 w-full rounded-xl shadow-sm"
            onClick={() => {
              window.location.assign("/sso/au/start");
            }}
          >
            {t("使用教育学院统一登录", "Sign in with AU")}
          </Button>
        )}

        {/* Sign up */}
        <p className="text-sm text-muted-foreground">
          {t("还没有账号？", "Need an account?")}{" "}
          <Link href="/signup" className="text-foreground font-medium hover:underline transition-colors">
            {t("立即注册", "Sign up")}
          </Link>
        </p>
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-5 z-10 px-4 text-center">
        <a
          href="https://beian.miit.gov.cn/"
          target="_blank"
          rel="noreferrer"
          className="pointer-events-auto text-xs tracking-[0.04em] text-[rgba(32,76,43,0.42)] no-underline transition-colors duration-300 hover:text-[rgba(32,76,43,0.76)]"
        >
          渝ICP备2025074845号-1
        </a>
      </div>
    </div>
  );
}
